class ElevenLabsWebSocket {
  constructor(
    apiKey,
    voiceId = "qS8z9VGvq5TIqGvrjctF",
    model = "eleven_turbo_v2_5",  // Changed to turbo for better quality/latency balance
    useProgressivePlayback = true,  // Feature flag: set to false to revert to wait-for-all-chunks behavior
    disableInternalPlayback = false  // NEW: Set to true to completely disable internal audio playback (for external playback control)
  ) {
    this.apiKey = apiKey;
    this.voiceId = voiceId;
    this.model = model;
    this.websocket = null;
    this.audioBuffer = [];
    this.audioContext = null;
    this.audioSource = null;
    this.isPlaying = false;
    this.isStreamComplete = false;
    this.isFirstPlay = true;
    this.accumulatedChunks = 0;
    this.playedChunks = 0;
    this.lastPlayedChunkIndex = -1;  // Track last chunk index that was played
    this.MIN_CHUNKS_TO_PLAY = 2;
    this.useProgressivePlayback = useProgressivePlayback;  // FEATURE FLAG: Enable progressive playback for lower latency
    this.disableInternalPlayback = disableInternalPlayback;  // FEATURE FLAG: Disable all internal playback (just buffer chunks)
    this.isUpdating = false;
    this.allChunksReceived = false;
    this.totalAudioDuration = 0;
    this.callbacks = {
      onStart: null,
      onAudioChunk: null,
      onChunksReady: null,  // NEW: Fires when MIN_CHUNKS_TO_PLAY reached (for fast external playback)
      onComplete: null,
      onError: null,
      onPlaybackStart: null,
      onPlaybackEnd: null,
    };
  }

  // Set callback functions
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // Initialize audio context
  async initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
    }

    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  // Validate and clean base64 string
  cleanBase64(base64String) {
    // Remove any whitespace and invalid characters
    let cleaned = base64String.replace(/[^A-Za-z0-9+/=]/g, "");

    // Ensure proper padding
    while (cleaned.length % 4 !== 0) {
      cleaned += "=";
    }

    return cleaned;
  }

  // Validate if string is valid base64
  isValidBase64(str) {
    try {
      const cleaned = this.cleanBase64(str);
      return btoa(atob(cleaned)) === cleaned;
    } catch (err) {
      return false;
    }
  }

  // Convert base64 to binary data
  base64ToBinary(base64String) {
    try {
      const cleaned = this.cleanBase64(base64String);
      const binaryString = atob(cleaned);
      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      return bytes;
    } catch (error) {
      console.error("Error converting base64 to binary:", error);
      return null;
    }
  }

  // Convert base64 audio to audio buffer
  async base64ToAudioBuffer(base64Audio) {
    try {
      const binaryData = this.base64ToBinary(base64Audio);
      if (!binaryData) throw new Error("Failed to convert base64 to binary");

      const audioBuffer = await this.audioContext.decodeAudioData(
        binaryData.buffer.slice()
      );
      return audioBuffer;
    } catch (error) {
      console.error("Error converting base64 to audio buffer:", error);
      throw error;
    }
  }

  // Create audio blob by concatenating binary data (not base64)
  createAudioBlob(startIndex = 0, endIndex = null) {
    if (
      this.audioBuffer.length === 0 ||
      startIndex >= this.audioBuffer.length
    ) {
      console.log("No audio buffer or invalid start index:", {
        bufferLength: this.audioBuffer.length,
        startIndex,
      });
      return null;
    }

    try {
      // Use chunks from startIndex to endIndex (or end)
      const actualEndIndex = endIndex || this.audioBuffer.length;
      const chunksToPlay = this.audioBuffer.slice(startIndex, actualEndIndex);

      console.log("Creating audio blob:", {
        startIndex,
        endIndex: actualEndIndex,
        chunksToPlay: chunksToPlay.length,
        totalChunks: this.audioBuffer.length,
        chunkSizes: chunksToPlay.map((chunk) => chunk.length),
      });

      // Convert each base64 chunk to binary and collect them
      const binaryChunks = [];
      let totalSize = 0;

      for (let i = 0; i < chunksToPlay.length; i++) {
        const chunk = chunksToPlay[i];
        if (this.isValidBase64(chunk)) {
          const binaryData = this.base64ToBinary(chunk);
          if (binaryData) {
            binaryChunks.push(binaryData);
            totalSize += binaryData.length;
          }
        } else {
          console.warn(
            `Invalid base64 chunk at index ${i}, skipping:`,
            chunk.substring(0, 50) + "..."
          );
        }
      }

      if (binaryChunks.length === 0) {
        console.error("No valid binary chunks found");
        return null;
      }

      console.log(
        `Using ${binaryChunks.length} valid chunks out of ${chunksToPlay.length} total chunks`
      );
      console.log("Total binary size:", totalSize, "bytes");

      // Concatenate all binary chunks into one Uint8Array
      const concatenatedBinary = new Uint8Array(totalSize);
      let offset = 0;

      for (const binaryChunk of binaryChunks) {
        concatenatedBinary.set(binaryChunk, offset);
        offset += binaryChunk.length;
      }

      console.log(
        "Concatenated binary size:",
        concatenatedBinary.length,
        "bytes"
      );

      // Create blob from concatenated binary data
      const blob = new Blob([concatenatedBinary], { type: "audio/mpeg" });
      console.log("Created blob size:", blob.size, "bytes");

      return blob;
    } catch (error) {
      console.error("Error creating audio blob:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        chunksCount: this.audioBuffer.length,
        startIndex,
        endIndex,
      });
      return null;
    }
  }

  // Wait for all chunks then play complete audio
  async waitAndPlayCompleteAudio() {
    console.log("Waiting for all chunks to be received...");

    // Wait until all chunks are received
    const checkInterval = setInterval(() => {
      if (this.allChunksReceived) {
        clearInterval(checkInterval);
        console.log("All chunks received, playing complete audio");
        this.playCompleteAudio();
      }
    }, 100);
  }

  // Play complete audio with all chunks
  async playCompleteAudio() {
    // Skip internal playback if disabled (for external playback control)
    if (this.disableInternalPlayback) {
      console.log('Internal playback disabled, skipping playCompleteAudio()');
      return;
    }

    if (this.audioBuffer.length === 0) {
      console.log("No audio buffer to play");
      return;
    }

    // Calculate which chunks to play (only new ones after last played)
    const startIndex = this.lastPlayedChunkIndex + 1;
    const endIndex = this.audioBuffer.length;

    if (startIndex >= endIndex) {
      console.log("No new chunks to play", { startIndex, endIndex });
      return;
    }

    // Stop any existing audio
    if (this.audioSource && this.isPlaying) {
      this.audioSource.pause();
      this.audioSource.currentTime = 0;
      if (this.audioSource.src) {
        URL.revokeObjectURL(this.audioSource.src);
      }
    }

    try {
      console.log("Playing audio chunks:", {
        startIndex,
        endIndex,
        newChunks: endIndex - startIndex,
        totalChunks: this.audioBuffer.length,
        totalBufferSize: this.audioBuffer.reduce(
          (sum, chunk) => sum + chunk.length,
          0
        ),
        lastPlayedChunkIndex: this.lastPlayedChunkIndex,
      });

      const audioBlob = this.createAudioBlob(startIndex, endIndex);

      // Update last played chunk index
      this.lastPlayedChunkIndex = endIndex - 1;
      if (!audioBlob) {
        console.error("Failed to create complete audio blob");
        if (this.callbacks.onError) {
          this.callbacks.onError(
            new Error("Failed to create audio from received chunks")
          );
        }
        return;
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      console.log("Created complete audio URL:", audioUrl);

      // Create new audio element
      this.audioSource = new Audio(audioUrl);
      this.audioSource.preload = "auto";

      this.audioSource.onloadeddata = () => {
        console.log("Complete audio loaded successfully:", {
          duration: this.audioSource.duration,
          readyState: this.audioSource.readyState,
          totalChunks: this.audioBuffer.length,
        });
      };

      this.audioSource.onended = () => {
        console.log("Complete audio playback finished");
        this.isPlaying = false;
        URL.revokeObjectURL(audioUrl);

        // Progressive playback: Check if there are more chunks to play
        if (this.useProgressivePlayback && this.lastPlayedChunkIndex < this.audioBuffer.length - 1) {
          console.log(`Progressive playback: Audio ended, playing remaining chunks (${this.lastPlayedChunkIndex + 1} to ${this.audioBuffer.length - 1})`);
          this.playCompleteAudio();
          return; // Don't call callbacks yet - more chunks to play
        }

        // All chunks have been played - now call callbacks
        console.log("All audio chunks played, calling completion callbacks");

        if (this.callbacks.onPlaybackEnd) {
          this.callbacks.onPlaybackEnd();
        }

        if (this.callbacks.onComplete) {
          this.callbacks.onComplete();
        }
      };

      this.audioSource.onerror = (error) => {
        console.error("Audio playback error:", error);
        this.isPlaying = false;
        URL.revokeObjectURL(audioUrl);

        if (this.callbacks.onError) {
          this.callbacks.onError(error);
        }
      };

      this.audioSource.ontimeupdate = () => {
        if (this.audioSource && this.audioSource.duration) { // Added null check for this.audioSource
          const progress =
            (this.audioSource.currentTime / this.audioSource.duration) * 100;
          console.log(
            `Audio progress: ${progress.toFixed(
              1
            )}% (${this.audioSource.currentTime.toFixed(
              1
            )}s / ${this.audioSource.duration.toFixed(1)}s)`
          );
        }
      };

      // Start playback
      this.isPlaying = true;
      if (this.callbacks.onPlaybackStart) {
        this.callbacks.onPlaybackStart();
      }

      try {
        await this.audioSource.play();
        console.log(
          "Started playing complete audio, duration:",
          this.audioSource.duration
        );
      } catch (playError) {
        console.error("Error starting audio playback:", playError);
        this.isPlaying = false;
        if (this.callbacks.onError) {
          this.callbacks.onError(playError);
        }
      }
    } catch (error) {
      console.error("Error in playCompleteAudio:", error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    }
  }

  // Start streaming text-to-speech
  async startStreaming(text, options = {}) {
    try {
      if (this.callbacks.onStart) {
        this.callbacks.onStart();
      }

      // Reset state
      this.audioBuffer = [];
      this.accumulatedChunks = 0;
      this.playedChunks = 0;
      this.lastPlayedChunkIndex = -1;  // Reset chunk tracking
      this.isPlaying = false;
      this.isFirstPlay = true;
      this.isUpdating = false;
      this.allChunksReceived = false;
      this.totalAudioDuration = 0;

      const uri = `wss://api.elevenlabs.io/v1/text-to-speech/${this.voiceId}/stream-input?model_id=${this.model}`;
      console.log("Connecting to WebSocket:", uri);

      this.websocket = new WebSocket(uri);

      this.websocket.onopen = () => {
        console.log("WebSocket opened, sending initial config");
        
        // Prepare configuration with SSML support if needed
        const config = {
          text: " ",
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.8,
            use_speaker_boost: true,
            style: 1.0,
          },
          generation_config: {
            chunk_length_schedule: [50, 120, 150, 200], // Smaller initial chunks for faster response
            stream_chunk_size: 1024,
          },
          xi_api_key: this.apiKey,
        };
        
        console.log('ElevenLabsWebSocket: Sending config');
        this.websocket?.send(JSON.stringify(config));

        // Send text in smaller chunks for better streaming
        const chunkSize = 200; // Reduced from 500 for lower latency
        for (let i = 0; i < text.length; i += chunkSize) {
          const chunk = text.slice(i, i + chunkSize);
          this.websocket?.send(JSON.stringify({ text: chunk }));
        }

        // Send empty text to signal end
        this.websocket?.send(JSON.stringify({ text: "" }));
      };

      this.websocket.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          // Only process if we have actual audio data
          if (data.audio && data.audio !== null) {
            // Validate the audio chunk before adding to buffer
            if (this.isValidBase64(data.audio)) {
              this.audioBuffer.push(data.audio);
              this.accumulatedChunks++;

              console.log("Received valid audio chunk", {
                chunk: this.accumulatedChunks,
                chunkSize: data.audio.length,
                bufferLength: this.audioBuffer.length,
                totalBufferSize: this.audioBuffer.reduce(
                  (sum, chunk) => sum + chunk.length,
                  0
                ),
                isFirstPlay: this.isFirstPlay,
                isPlaying: this.isPlaying,
                allChunksReceived: this.allChunksReceived,
              });

              if (this.callbacks.onAudioChunk) {
                this.callbacks.onAudioChunk(data.audio, this.accumulatedChunks);
              }

              // PROGRESSIVE PLAYBACK: Start playing after MIN_CHUNKS_TO_PLAY
              // OR WAIT FOR ALL: Wait for all chunks before playing (old behavior)
              // SKIP IF: disableInternalPlayback is true (external playback control)
              if (!this.disableInternalPlayback) {
                if (this.isFirstPlay) {
                  if (this.useProgressivePlayback) {
                    // NEW BEHAVIOR: Start playing after MIN_CHUNKS_TO_PLAY chunks
                    if (this.accumulatedChunks >= this.MIN_CHUNKS_TO_PLAY) {
                      console.log(`Progressive playback: Starting after ${this.accumulatedChunks} chunks`);
                      this.isFirstPlay = false;
                      this.playCompleteAudio(); // Play what we have so far
                    }
                  } else {
                    // OLD BEHAVIOR: Wait for all chunks (safe fallback)
                    if (this.accumulatedChunks === 1) {
                      console.log("Legacy mode: Starting to wait for all chunks");
                      this.isFirstPlay = false;
                      this.waitAndPlayCompleteAudio();
                    }
                  }
                } else if (this.useProgressivePlayback && !this.isPlaying && this.lastPlayedChunkIndex < this.audioBuffer.length - 1) {
                  // Progressive playback: If we're not playing and have new chunks, play them
                  console.log(`Progressive playback: Queueing next segment (chunks ${this.lastPlayedChunkIndex + 1} to ${this.audioBuffer.length - 1})`);
                  this.playCompleteAudio();
                }
              }

              // Early chunk notification for external playback (generation-only mode)
              // This gives fast blob URL access without triggering internal playback
              if (this.disableInternalPlayback &&
                  this.accumulatedChunks >= this.MIN_CHUNKS_TO_PLAY &&
                  this.isFirstPlay) {
                this.isFirstPlay = false;
                console.log(`[ElevenLabsWebSocket] Generation-only: ${this.accumulatedChunks} chunks ready for early playback`);

                if (this.callbacks.onChunksReady) {
                  const earlyBlob = this.createAudioBlob(0, this.audioBuffer.length);
                  if (earlyBlob) {
                    const earlyBlobUrl = URL.createObjectURL(earlyBlob);
                    console.log(`[ElevenLabsWebSocket] Calling onChunksReady with early blob URL`);
                    this.callbacks.onChunksReady(earlyBlobUrl);
                  }
                }
              }
            } else {
              console.warn("Received invalid base64 audio chunk, skipping");
            }
          } else if (data.audio === null) {
            console.log("Received null audio chunk - no more audio data");
          }

          if (data.isFinal) {
            console.log("Received final flag - all chunks received", {
              totalChunks: this.audioBuffer.length,
              totalBufferSize: this.audioBuffer.reduce(
                (sum, chunk) => sum + chunk.length,
                0
              ),
              chunkSizes: this.audioBuffer.map((chunk) => chunk.length),
              validChunks: this.audioBuffer.filter((chunk) =>
                this.isValidBase64(chunk)
              ).length,
              isFirstPlay: this.isFirstPlay,
              isPlaying: this.isPlaying,
            });

            this.allChunksReceived = true;
            this.isStreamComplete = true;

            // If we haven't started the waiting process, start playing now
            if (this.isFirstPlay && this.audioBuffer.length > 0) {
              console.log("Starting immediate playback with all chunks");
              this.isFirstPlay = false;
              await this.playCompleteAudio();
            }
          }
        } catch (error) {
          console.error("Error processing WebSocket message:", error);
          if (this.callbacks.onError) {
            this.callbacks.onError(error);
          }
        }
      };

      this.websocket.onerror = (error) => {
        console.error("WebSocket error:", error);
        if (this.callbacks.onError) {
          this.callbacks.onError(error);
        }
        this.stop();
      };

      this.websocket.onclose = (event) => {
        console.log("WebSocket closed", event.code, event.reason);

        this.allChunksReceived = true;

        if (!this.disableInternalPlayback) {
          // INTERNAL PLAYBACK MODE: Play audio, onComplete will be called when playback finishes
          if (this.audioBuffer.length > 0 && !this.isPlaying) {
            console.log("WebSocket closed, playing all remaining audio:", {
              totalChunks: this.audioBuffer.length,
              validChunks: this.audioBuffer.filter((chunk) =>
                this.isValidBase64(chunk)
              ).length,
              totalSize: this.audioBuffer.reduce(
                (sum, chunk) => sum + chunk.length,
                0
              ),
            });
            this.playCompleteAudio();
          }
        } else {
          // GENERATION-ONLY MODE: Don't play internally, but signal that audio is ready
          console.log("WebSocket closed, audio generation complete (external playback mode):", {
            totalChunks: this.audioBuffer.length,
            validChunks: this.audioBuffer.filter((chunk) =>
              this.isValidBase64(chunk)
            ).length,
            totalSize: this.audioBuffer.reduce(
              (sum, chunk) => sum + chunk.length,
              0
            ),
          });
          if (this.callbacks.onComplete) {
            this.callbacks.onComplete();
          }
        }

        this.websocket = null;
      };
    } catch (error) {
      console.error("Error starting streaming:", error);
      if (this.callbacks.onError) {
        this.callbacks.onError(error);
      }
    }
  }

  // Stop streaming and audio playback
  stop() {
    console.log("Stopping streaming and audio");

    // Close WebSocket
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }

    // Stop audio playback
    if (this.audioSource && this.isPlaying) {
      this.audioSource.pause();
      this.audioSource.currentTime = 0;
      if (this.audioSource.src) {
        URL.revokeObjectURL(this.audioSource.src);
      }
      this.audioSource = null;
    }

    // Reset state
    this.isPlaying = false;
    this.isStreamComplete = false;
    this.isUpdating = false;
    this.audioBuffer = [];
    this.accumulatedChunks = 0;
    this.playedChunks = 0;
    this.lastPlayedChunkIndex = -1;  // Reset chunk tracking
    this.isFirstPlay = true;
    this.allChunksReceived = false;
    this.totalAudioDuration = 0;

    console.log("Streaming stopped");
  }

  // Get current state
  getState() {
    return {
      isPlaying: this.isPlaying,
      isStreamComplete: this.isStreamComplete,
      bufferedChunks: this.audioBuffer.length,
      playedChunks: this.playedChunks,
      hasWebSocket: !!this.websocket,
      accumulatedChunks: this.accumulatedChunks,
      allChunksReceived: this.allChunksReceived,
      totalBufferSize: this.audioBuffer.reduce(
        (sum, chunk) => sum + chunk.length,
        0
      ),
      validChunks: this.audioBuffer.filter((chunk) => this.isValidBase64(chunk))
        .length,
    };
  }
}

export default ElevenLabsWebSocket;
